import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import { debounce } from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'
import moment from "moment";

export default function useList() {
  const refListTable = ref(null)
  const toast = useToast()

  const tableColumns = [
    {
      label: 'pacom.id',
      key: 'id'
    },
    {
      label: 'pacom.color',
      key: 'color'
    },
    {
      label: 'pacom.type',
      key: 'type'
    },
    {
      label: 'pacom.device',
      key: 'device.name'
    },
    {
      label: 'pacom.idAlarm',
      key: 'id_pacom_log'
    },
    {
      label: 'pacom.typeAlarm',
      key: 'alarm',
      formatter: (value) => {
        if (!value) {
          return '';
        }

        let { alarm_type } = value;
        let { type_alarm } = alarm_type;

        return `${type_alarm.description} (${alarm_type.name})`
      }
    },
    {
      label: 'pacom.schemaEvent',
      key: 'schema_event',
      formatter: (value) => {
        if (!value) {
          return '';
        }

        let { display_name } = value;

        return display_name;
      }
    },
    {
      label: 'pacom.time',
      key: 'time',
      formatter: (value) => {
        return moment.utc(value).tz('Etc/GMT+6').format('YYYY-MM-DD HH:mm:ss')
      }
    }
  ]

  const time = ref(null)
  const type = ref(null)

  const timeFilter = ref(null)
  const startDatetimeFilter = ref(null)
  const endDatetimeFilter = ref(null)
  const typeFilter = ref(null)
  const idFilter = ref(null)

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const filters = ref({});

  const filtersCount = useFilterCounter(filters);

  const buildFilters = () => {
    filters.value = {
      startDate: startDatetimeFilter.value,
      endDate: endDatetimeFilter.value,
      type: typeFilter.value,
      id: idFilter.value
    }

    refetchData();
  }

  const clearSearchFilters = () => {
    typeFilter.value = null;
    startDatetimeFilter.value = null;
    endDatetimeFilter.value = null;
    idFilter.value = null;

    buildFilters();
  }

  const fetchList = (ctx, callback) => {
    store.dispatch('pacom_logs/getAll', {
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      type: typeFilter.value,
      startDate: startDatetimeFilter.value,
      endDate: endDatetimeFilter.value,
      id: idFilter.value
    })
      .then(response => {
        callback(response.data)
        totalList.value = response.meta.total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([currentPage, perPage], () => {
    refetchData()
  })

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    timeFilter,
    typeFilter,
    refetchData,
    filtersCount,
    clearSearchFilters,
    buildFilters,
    startDatetimeFilter,
    endDatetimeFilter,
    type,
    idFilter
  }
}
